import React, { createContext, useContext, useRef } from "react";
import { useLocation } from "react-router-dom";
import { IBreadcrumbProps } from "../../layout/fullLayout/breadcrumb/IBreadcrumbProps";
import { Breadcrumb } from "../../layout/fullLayout/breadcrumb/Breadcrumb";

export interface IBreadcrumbContext {
  addBreadcrumb: (data: IBreadcrumbProps) => void;
  removeBreadcrumb: () => void;
  breadcrumbData: IBreadcrumbProps | null;
}

const BreadcrumbContext = createContext<IBreadcrumbContext>({
  addBreadcrumb: () => {},
  removeBreadcrumb: () => {},
  breadcrumbData: null,
});

export const useBreadcrumbContext = () => useContext(BreadcrumbContext);

export const BreadcrumbProvider: React.FC<{
  children: React.ReactNode;
  renderInline?: boolean;
}> = ({ children, renderInline = true }) => {
  const [state, setState] = React.useState<IBreadcrumbProps | null>(null);
  const location = useLocation();
  const prevPathnameRef = useRef(location.pathname);

  // Reset breadcrumb synchronously during render when pathname changes
  // This avoids the race condition with child useEffect calls
  if (prevPathnameRef.current !== location.pathname) {
    prevPathnameRef.current = location.pathname;
    if (state !== null) {
      setState(null);
    }
  }

  const handleAddBreadcrub = (data: IBreadcrumbProps) => {
    setState(data);
  };

  const handleRemove = () => {
    setState(null);
  };

  return (
    <BreadcrumbContext.Provider
      value={{
        addBreadcrumb: handleAddBreadcrub,
        removeBreadcrumb: handleRemove,
        breadcrumbData: state,
      }}
    >
      {renderInline && state && <Breadcrumb {...state} />}
      {children}
    </BreadcrumbContext.Provider>
  );
};
